import { type SceneKeyframe, phaseAt } from '../scene-clock'

/**
 * Timeline and demo data for the landlord "Get Back To Them In 2 Minutes"
 * scene: a notification on a locked phone, a tap through four verified
 * applications, four verification checks and an approval, then the popout
 * with the time it took. Everything here is pure; the component only maps
 * the phase to classes.
 *
 * Milliseconds are those of the reference (one 12.5 s loop).
 */

export const APPROVE_LOOP_MS = 12_500

export const APPROVE_PHASES = [
	'idle',
	'notify',
	'tapNotify',
	'hitNotify',
	'open',
	'hover3',
	'hover2',
	'hover1',
	'hover0',
	'select',
	'listOut',
	'detail',
	'check0',
	'check1',
	'check2',
	'check3',
	'tapApprove',
	'hitApprove',
	'approved',
	'popout',
] as const

export type ApprovePhase = (typeof APPROVE_PHASES)[number]

export const APPROVE_FINAL_PHASE: ApprovePhase = 'popout'

export const APPROVE_KEYFRAMES: readonly SceneKeyframe<ApprovePhase>[] = [
	{ at: 0, phase: 'idle' },
	{ at: 500, phase: 'notify' },
	{ at: 1500, phase: 'tapNotify' },
	{ at: 1900, phase: 'hitNotify' },
	{ at: 2150, phase: 'open' },
	{ at: 2900, phase: 'hover3' },
	{ at: 3320, phase: 'hover2' },
	{ at: 3740, phase: 'hover1' },
	{ at: 4160, phase: 'hover0' },
	{ at: 4600, phase: 'select' },
	{ at: 4850, phase: 'listOut' },
	{ at: 5080, phase: 'detail' },
	{ at: 5450, phase: 'check0' },
	{ at: 5780, phase: 'check1' },
	{ at: 6110, phase: 'check2' },
	{ at: 6440, phase: 'check3' },
	{ at: 6700, phase: 'tapApprove' },
	{ at: 7250, phase: 'hitApprove' },
	{ at: 7450, phase: 'approved' },
	{ at: 8000, phase: 'popout' },
]

/** The "Time to Approve" stopwatch: linear from 0:00 to 0:45 once the app opens. */
export const APPROVE_CLOCK = {
	startMs: 2150,
	durationMs: 5600,
	targetSeconds: 45,
} as const

export function approvePhaseAt(elapsedMs: number): ApprovePhase {
	return phaseAt(elapsedMs, APPROVE_KEYFRAMES, APPROVE_LOOP_MS)
}

/** Whole seconds on the stopwatch `elapsedMs` into the (looping) scene. */
export function approveClockSecondsAt(elapsedMs: number): number {
	const t = ((elapsedMs % APPROVE_LOOP_MS) + APPROVE_LOOP_MS) % APPROVE_LOOP_MS
	const { startMs, durationMs, targetSeconds } = APPROVE_CLOCK
	if (t < startMs) return 0
	const k = Math.min(1, (t - startMs) / durationMs)
	return Math.round(targetSeconds * k)
}

export function approvePhaseIndex(phase: ApprovePhase): number {
	return APPROVE_PHASES.indexOf(phase)
}

/** True once the scene has reached `target` (or is past it). */
export function approveReached(
	phase: ApprovePhase,
	target: ApprovePhase,
): boolean {
	return approvePhaseIndex(phase) >= approvePhaseIndex(target)
}

export type TapTarget = 'notify' | 'row3' | 'row2' | 'row1' | 'row0' | 'approve'

export interface ApproveSceneState {
	/** The lock-screen notification card. */
	notification: 'hidden' | 'in' | 'pressed'
	/**
	 * The translucent finger. `target` is where it is parked even while
	 * hidden, so a fade-in glides from the last spot as the reference does.
	 */
	tap: { target: TapTarget; visible: boolean; hit: boolean }
	/** The lock screen has faded and the app is showing. */
	unlocked: boolean
	/** Row under the finger in the list, if any. */
	hoveredRow: number | null
	/** Row 0 carries the ink border once tapped. */
	selectedRow: boolean
	list: 'shown' | 'fading' | 'gone'
	detail: boolean
	/** How many of the four verification checks have popped. */
	checks: number
	approve: 'idle' | 'pressed' | 'approved'
	/** The phone has slid left and the popout is showing. */
	moved: boolean
}

const HOVER_ROW: Partial<Record<ApprovePhase, number>> = {
	hover3: 3,
	hover2: 2,
	hover1: 1,
	hover0: 0,
	select: 0,
	listOut: 0,
}

function tapAt(phase: ApprovePhase): ApproveSceneState['tap'] {
	switch (phase) {
		case 'idle':
		case 'notify':
			return { target: 'notify', visible: false, hit: false }
		case 'tapNotify':
			return { target: 'notify', visible: true, hit: false }
		case 'hitNotify':
			return { target: 'notify', visible: true, hit: true }
		case 'open':
			return { target: 'notify', visible: false, hit: false }
		case 'hover3':
			return { target: 'row3', visible: true, hit: false }
		case 'hover2':
			return { target: 'row2', visible: true, hit: false }
		case 'hover1':
			return { target: 'row1', visible: true, hit: false }
		case 'hover0':
			return { target: 'row0', visible: true, hit: false }
		case 'select':
			return { target: 'row0', visible: true, hit: true }
		case 'listOut':
		case 'detail':
		case 'check0':
		case 'check1':
		case 'check2':
		case 'check3':
			return { target: 'row0', visible: false, hit: false }
		case 'tapApprove':
			return { target: 'approve', visible: true, hit: false }
		case 'hitApprove':
			return { target: 'approve', visible: true, hit: true }
		case 'approved':
		case 'popout':
			return { target: 'approve', visible: false, hit: false }
	}
}

/** Everything the component needs to draw one phase. */
export function approveStateAt(phase: ApprovePhase): ApproveSceneState {
	const reached = (target: ApprovePhase) => approveReached(phase, target)
	const checks = reached('check3')
		? 4
		: reached('check2')
			? 3
			: reached('check1')
				? 2
				: reached('check0')
					? 1
					: 0
	return {
		notification: reached('hitNotify')
			? 'pressed'
			: reached('notify')
				? 'in'
				: 'hidden',
		tap: tapAt(phase),
		unlocked: reached('open'),
		hoveredRow: HOVER_ROW[phase] ?? null,
		selectedRow: reached('select'),
		list: reached('detail') ? 'gone' : reached('listOut') ? 'fading' : 'shown',
		detail: reached('detail'),
		checks,
		approve: reached('approved')
			? 'approved'
			: phase === 'hitApprove'
				? 'pressed'
				: 'idle',
		moved: reached('popout'),
	}
}

/* ---------- demo data (the reference's, verbatim) ---------- */

export type AvatarTone = 'sage' | 'lav' | 'pink'

export interface ApproveApplicant {
	name: string
	avatars: { initials: string; tone: AvatarTone }[]
}

export const APPROVE_SCENE = {
	address: '214 Queen St W',
	lock: { date: 'Tuesday, October 6', time: '10:02' },
	notification: {
		title: '4 Verified Applications',
		body: '214 Queen St W is ready to review.',
		when: 'now',
	},
	bar: { label: 'Time to Approve' },
	list: { subtitle: '4 verified applications', back: 'Applications' },
	applicants: [
		{ name: 'Sarah Johnson', avatars: [{ initials: 'SJ', tone: 'sage' }] },
		{
			name: 'Daniel Kim & Priya Shah',
			avatars: [
				{ initials: 'DK', tone: 'lav' },
				{ initials: 'PS', tone: 'pink' },
			],
		},
		{ name: 'Amy Liu', avatars: [{ initials: 'AL', tone: 'pink' }] },
		{
			name: 'Raj Patel & Maya Brooks',
			avatars: [
				{ initials: 'RP', tone: 'sage' },
				{ initials: 'MB', tone: 'lav' },
			],
		},
	] satisfies ApproveApplicant[],
	verifications: [
		{ label: 'ID Verified' },
		{ label: 'Income Verified', value: '$8,000', unit: 'a month' },
		{ label: 'Rent Range Eligibility', value: '$3,000 to $3,500' },
		{ label: 'Credit Report' },
	],
	approve: { idle: 'Approve Applicant', done: 'Approved' },
	popout: {
		title: 'Sarah Johnson Approved',
		approvedIn: 'Approved in',
		time: '0:45',
		next: 'Send Lease & Collect LMR',
	},
} as const
